import { useHead } from '@vueuse/head'
import { useI18n } from 'vue-i18n'

export interface StructuredData {
  '@context'?: string
  '@type': string
  [key: string]: any
}

export function useSeoMeta(options: {
  title?: string
  description?: string
  keywords?: string
  ogTitle?: string
  ogDescription?: string
  ogImage?: string
  ogUrl?: string
  canonical?: string
  robots?: string
  structuredData?: StructuredData | StructuredData[]
}) {
  const { t } = useI18n()

  const defaultTitle = t('seo.defaultTitle', 'FLASHBASE | 美派时MEXPRESS | 48小时派对定制专家')
  const defaultDescription = t('seo.defaultDescription', '美派时MEXPRESS —— 源自美国派对文化的团体仪式感定制专家！专注为企业/团体/个人提供48小时极速交付的柔性供应链服务。在线选品、自由设计、智能生产、物流直达，一站式完成T恤/卫衣/软壳衣等高品质定制。用高效定制，点亮每一次相聚的仪式感！')
  const defaultKeywords = t('seo.defaultKeywords', '团体定制, 48小时定制, 柔性供应链, 在线设计平台, T恤定制, 卫衣定制, 企业礼品定制, 活动服装定制, 美派时, MEXPRESS, 派对文化品牌, 一站式定制平台, 快速打样, 柔性生产')

  const title = options.title ? `${options.title} - ${defaultTitle}` : defaultTitle
  const description = `${options.description || ''} , ${defaultDescription}`
  const keywords = `${options.keywords || ''} , ${defaultKeywords}`
  const ogTitle = options.ogTitle || title
  const ogDescription = options.ogDescription || description
  const ogImage = options.ogImage || '/favicon.ico'
  const ogUrl = options.ogUrl || window.location.href
  const canonical = options.canonical || window.location.href
  const robots = options.robots || 'index, follow, max-image-preview:large'

  const metaTags = [
    { name: 'description', content: description },
    { name: 'keywords', content: keywords },
    { property: 'og:title', content: ogTitle },
    { property: 'og:description', content: ogDescription },
    { property: 'og:image', content: ogImage },
    { property: 'og:url', content: ogUrl },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: 'FLASHBASE' },
    { property: 'og:locale', content: 'zh_CN' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: ogTitle },
    { name: 'twitter:description', content: ogDescription },
    { name: 'twitter:image', content: ogImage },
    { name: 'robots', content: robots }
  ]

  const linkTags = [
    { rel: 'canonical', href: canonical }
  ]

  // 处理JSON-LD结构化数据
  const scriptTags: any[] = []
  if (options.structuredData) {
    const structuredDataArray = Array.isArray(options.structuredData)
      ? options.structuredData
      : [options.structuredData]

    structuredDataArray.forEach((data, index) => {
      const scriptTag = {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          ...data
        })
      }

      scriptTags.push(scriptTag)
    })
  }

  useHead({
    title,
    meta: metaTags,
    link: linkTags,
    ...(scriptTags.length > 0 && { script: scriptTags })
  })
}

// 页面特定的SEO配置助手函数
export function usePageSeo(pageType: 'home' | 'product' | 'design' | 'about' | 'contact', pageData?: any) {
  const { t } = useI18n()

  const pageConfigs: any = {
    home: {
      title: `${t('seo.home.title', '首页 | FLASHBASE | 美派时柔性定制')}`,
      description: t('seo.home.description', '美派时柔性定制 - 48小时派对定制专家，提供高品质团体服装定制服务'),
      keywords: t('seo.home.keywords', '美派时,柔性定制,快速定制,服装定制'),
      structuredData: [createWebsiteStructuredData(), createOrganizationStructuredData()]
    },
    product: {
      title: pageData?.name ? `${pageData.name} - 产品详情` : `${t('seo.product.title', '产品中心 | FLASHBASE | 美派时柔性定制')}`,
      description: pageData?.description || t('seo.product.description', '浏览我们的定制产品系列，选择适合您的团体服装'),
      keywords: t('seo.product.keywords', '美派时,柔性定制,定制,T恤定制,卫衣定制'),
      structuredData: pageData ? createProductStructuredData(pageData) : createProductCollectionStructuredData()
    },
    design: {
      title: `${t('seo.design.title', '在线设计 | FLASHBASE | 美派时柔性定制')}`,
      description: t('seo.design.description', '使用我们的在线设计工具，轻松创建专属的团体服装设计'),
      keywords: t('seo.design.keywords', '在线设计,服装设计,定制设计'),
      structuredData: createServiceStructuredData()
    },
    about: {
      title: `${t('seo.about.title', '关于我们 | FLASHBASE | 美派时柔性定制')}`,
      description: t('seo.about.description', '了解美派时柔性定制的品牌故事和定制服务理念'),
      keywords: t('seo.about.keywords', '美派时,柔性定制,关于我们,定制理念'),
      structuredData: createOrganizationStructuredData()
    },
    contact: {
      title: `${t('seo.contact.title', '联系我们 | FLASHBASE | 美派时柔性定制')}`,
      description: t('seo.contact.description', '获取美派时柔性定制的联系方式和客户服务信息'),
      keywords: t('seo.contact.keywords', '美派时,柔性定制,联系方式,客户服务,咨询'),
      structuredData: createContactPageStructuredData()
    }
  }

  const config = pageConfigs[pageType] || pageConfigs.home

  return useSeoMeta({
    title: config.title,
    description: config.description,
    keywords: config.keywords,
    structuredData: config.structuredData
  })
}

// JSON-LD结构化数据助手函数

/**
 * 创建网站结构化数据
 */
export function createWebsiteStructuredData(): StructuredData {
  const { t } = useI18n()

  return {
    '@type': 'WebSite',
    '@id': `${window.location.origin}/#website`,
    name: t('seo.structuredData.website.name', 'FLASHBASE | MEXPRESS'),
    url: window.location.origin,
    description: t('seo.structuredData.website.description', '48小时派对定制专家 - 一站式柔性供应链服务平台'),
  }
}

/**
 * 创建组织结构化数据
 */
export function createOrganizationStructuredData(): StructuredData {
  const { t } = useI18n()

  return {
    '@type': 'Organization',
    '@id': `${window.location.origin}/#organization`,
    name: t('seo.structuredData.organization.name', '美派时智能科技'),
    url: window.location.origin,
    alternateName: t('seo.structuredData.organization.alternateName', 'MEXPRESS'),
    description: t('seo.structuredData.organization.description', '源自美国派对文化的团体仪式感定制专家'),
    logo: `${window.location.origin}/favicon.ico`,
    sameAs: [
      'https://www.flashbase.com',
      'https://shop545n830e87675.1688.com',
      'https://www.douyin.com/user/MS4wLjABAAAAph5uzRAMQfYUUAL9vkkW1Q3hm-M1N_6HoaFxrUJOg7A'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: t('seo.structuredData.organization.telephone', '+86-18652956287'),
      contactType: t('seo.structuredData.organization.contactType', 'customer service'),
      email: 'robinsun@flashbase.com',
      availableLanguage: [t('seo.structuredData.organization.language.chinese', 'Chinese'), t('seo.structuredData.organization.language.english', 'English')]
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: t('seo.structuredData.organization.address.street', '江苏省南京市江宁区东山街道丰泽路28号'),
      addressLocality: t('seo.structuredData.organization.address.city', '南京市'),
      postalCode: t('seo.structuredData.organization.address.postalCode', '210000'),
      addressCountry: t('seo.structuredData.organization.address.country', 'CN')
    }
  }
}

/**
 * 创建产品结构化数据
 */
export function createProductStructuredData(productData: any): StructuredData {
  const { t } = useI18n()

  return {
    '@type': 'Product',
    name: productData.name || t('seo.structuredData.product.defaultName', '定制产品'),
    description: productData.description || t('seo.structuredData.product.defaultDescription', '高品质团体服装定制产品'),
    image: productData.image || `${window.location.origin}/images/default-product.jpg`,
    sku: productData.sku || '',
    mpn: productData.mpn || '',
    brand: {
      '@type': 'Brand',
      name: t('seo.structuredData.product.brand', 'MEXPRESS')
    },
    offers: {
      '@type': 'Offer',
      url: window.location.href,
      priceCurrency: t('seo.structuredData.product.currency', 'CNY'),
      price: productData.price || 0,
      priceValidUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      availability: productData.availability || 'https://schema.org/InStock',
      seller: {
        '@type': 'Organization',
        name: t('seo.structuredData.product.seller', '美派时智能科技')
      }
    },
    aggregateRating: productData.rating ? {
      '@type': 'AggregateRating',
      ratingValue: productData.rating.value,
      reviewCount: productData.rating.count
    } : undefined
  }
}

/**
 * 创建产品集合结构化数据
 */
export function createProductCollectionStructuredData(): StructuredData {
  const { t } = useI18n()

  return {
    '@type': 'ItemList',
    name: t('seo.structuredData.productCollection.name', '定制产品系列'),
    description: t('seo.structuredData.productCollection.description', '美派时MEXPRESS定制产品系列'),
    numberOfItems: 0, // 可以在使用时动态设置
    itemListElement: [] // 可以在使用时动态添加产品
  }
}

/**
 * 创建服务结构化数据
 */
export function createServiceStructuredData(): StructuredData {
  const { t } = useI18n()

  return {
    '@type': 'Service',
    name: t('seo.structuredData.service.name', '在线设计服务'),
    description: t('seo.structuredData.service.description', '专业的团体服装在线设计服务'),
    provider: {
      '@type': 'Organization',
      name: t('seo.structuredData.service.provider', '美派时智能科技')
    },
    areaServed: t('seo.structuredData.service.areaServed', 'CN'),
    serviceType: t('seo.structuredData.service.serviceType', 'Custom Design Service'),
    offers: {
      '@type': 'Offer',
      description: t('seo.structuredData.service.offerDescription', '48小时快速定制服务')
    }
  }
}

/**
 * 创建联系页面结构化数据
 */
export function createContactPageStructuredData(): StructuredData {
  const { t } = useI18n()

  return {
    '@type': 'ContactPage',
    mainEntity: {
      '@type': 'Organization',
      name: t('seo.structuredData.contact.organization.name', '美派时智能科技'),
      description: t('seo.structuredData.contact.organization.description', '48小时派对定制专家'),
      telephone: t('seo.structuredData.contact.organization.telephone', '+86-18652956287'),
      email: 'robinsun@flashbase.com',
      url: window.location.origin
    }
  }
}

/**
 * 创建面包屑导航结构化数据
 */
export function createBreadcrumbStructuredData(breadcrumbs: Array<{ name: string, url: string }>): StructuredData {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  }
}

/**
 * 创建FAQ结构化数据
 */
export function createFAQStructuredData(questions: Array<{ question: string, answer: string }>): StructuredData[] {
  return questions.map(q => ({
    '@type': 'Question',
    name: q.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: q.answer
    }
  }))
}

/**
 * 创建博客文章列表结构化数据
 */
export function createBlogStructuredData(articles: Array<{
  title: string
  description: string
  url: string
  image?: string
  datePublished?: string
  author?: string
}>): StructuredData {
  const { t } = useI18n()

  return {
    '@type': 'Blog',
    name: t('seo.structuredData.blog.name', '定制指南与行业资讯'),
    description: t('seo.structuredData.blog.description', '美派时MEXPRESS定制指南、行业资讯和最新动态'),
    url: window.location.href,
    blogPost: articles.map(article => ({
      '@type': 'BlogPosting',
      headline: article.title,
      description: article.description,
      url: article.url,
      image: article.image,
      datePublished: article.datePublished,
      author: article.author ? {
        '@type': 'Person',
        name: article.author
      } : undefined
    }))
  }
}

/**
 * 创建单篇文章结构化数据
 */
export function createArticleStructuredData(articleData: {
  title: string
  description: string
  content: string
  image?: string
  datePublished: string
  dateModified?: string
  author?: string
  publisher?: string
}): StructuredData {
  const { t } = useI18n()

  return {
    '@type': 'Article',
    headline: articleData.title,
    description: articleData.description,
    articleBody: articleData.content,
    image: articleData.image,
    datePublished: articleData.datePublished,
    dateModified: articleData.dateModified || articleData.datePublished,
    author: articleData.author ? {
      '@type': 'Person',
      name: articleData.author
    } : undefined,
    publisher: {
      '@type': 'Organization',
      name: articleData.publisher || t('seo.structuredData.article.publisher', '美派时智能科技'),
      logo: {
        '@type': 'ImageObject',
        url: `${window.location.origin}/favicon.ico`
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': window.location.href
    }
  }
}
